import type { ReactNode } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  type SortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cx } from '@/components/ui';

/**
 * A list whose order is dragged rather than argued with.
 *
 * Built once and shared, because the same interaction is wanted in at least
 * three places — product images, home-page layout blocks, and the category
 * tree — and three hand-rolled versions would drift apart.
 *
 * Two deliberate choices:
 *
 *  - **Pointer drags need 6px of movement before they start.** Without that a
 *    click on a button inside a card is swallowed as a tiny drag, which is how
 *    a delete button stops working the day dragging is added.
 *  - **The keyboard sensor is included, not an afterthought.** Space picks a
 *    card up, the arrows move it, Space drops it. Dragging is the convenient
 *    path; it is never the only one, and the callers keep their arrow buttons.
 */
export function SortableGrid<T extends { id: string }>(props: SortableProps<T>) {
  return <Sortable {...props} strategy={rectSortingStrategy} modifiers={GRID_MODIFIERS} />;
}

/**
 * The same, for a column of cards of different heights — a product page's
 * description blocks. Cards move only up and down, and each makes room for
 * the one dragged past it at that card's own height, which a grid's strategy
 * does not.
 */
export function SortableList<T extends { id: string }>(props: SortableProps<T>) {
  return <Sortable {...props} strategy={verticalListSortingStrategy} modifiers={LIST_MODIFIERS} />;
}

interface SortableProps<T extends { id: string }> {
  items: T[];
  /** The full list in its new order — callers persist all of it, not a swap. */
  onReorder: (items: T[]) => void;
  disabled?: boolean;
  className?: string;
  children: (item: T, index: number) => ReactNode;
}

// Kept inside its own container: a card dragged across the page and dropped
// over an unrelated list is a confusing thing to explain.
const GRID_MODIFIERS: Modifier[] = [restrictToParentElement];
const LIST_MODIFIERS: Modifier[] = [restrictToVerticalAxis, restrictToParentElement];

function Sortable<T extends { id: string }>({
  items,
  onReorder,
  disabled = false,
  className,
  children,
  strategy,
  modifiers,
}: SortableProps<T> & { strategy: SortingStrategy; modifiers: Modifier[] }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const from = items.findIndex((item) => item.id === active.id);
    const to = items.findIndex((item) => item.id === over.id);
    if (from < 0 || to < 0) return;

    onReorder(arrayMove(items, from, to));
  };

  if (disabled) {
    return <ul className={className}>{items.map((item, index) => children(item, index))}</ul>;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={modifiers}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={items.map((item) => item.id)} strategy={strategy}>
        <ul className={className}>{items.map((item, index) => children(item, index))}</ul>
      </SortableContext>
    </DndContext>
  );
}

/**
 * One draggable cell.
 *
 * The drag listeners go on a **handle**, not the whole card: everything else in
 * it — a preview button, a delete button, the arrows — has to stay clickable.
 */
export function SortableItem({
  id,
  className,
  children,
}: {
  id: string;
  className?: string;
  children: (handle: { attributes: Record<string, unknown>; listeners: Record<string, unknown> }) => ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cx(className, isDragging && 'z-10 opacity-80')}
    >
      {children({
        attributes: attributes as unknown as Record<string, unknown>,
        listeners: (listeners ?? {}) as unknown as Record<string, unknown>,
      })}
    </li>
  );
}
